import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../redux/authSlice";
import { fetchDealerProfile } from "../redux/dealerSlice";
import { FiLogOut } from "react-icons/fi";
import type { RootState, AppDispatch } from "../redux/store";
import { useState, useEffect } from "react";
import { Sparkles } from "lucide-react";
import LogoutConfirmModal from "./common/LogoutConfirmModal";
import AiReportModal from "./dashboard/AiReportModal";
import { usePortalFeedback } from "../context/PortalFeedbackContext";
import { useActivityTracker } from "../hooks/useActivityTracker";
import decoIcon from "../icon.png";

export default function Navbar() {
  const { user } = useSelector((state: RootState) => state.auth);
  const { profile } = useSelector((state: RootState) => state.dealer);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isAiReportModalOpen, setIsAiReportModalOpen] = useState(false);
  const impersonationType = localStorage.getItem("type");
  const isImpersonating = impersonationType === "admin" || impersonationType === "url_impersonation";
  const { openFeedback } = usePortalFeedback();
  const { triggerLogoutActivity } = useActivityTracker(!!user && !isImpersonating);

  useEffect(() => {
    const handleOpenReport = () => setIsAiReportModalOpen(true);
    window.addEventListener("open-dealer-ai-report", handleOpenReport);
    return () => window.removeEventListener("open-dealer-ai-report", handleOpenReport);
  }, []);

  useEffect(() => {
    if (user && !profile) {
      dispatch(fetchDealerProfile());
    }
  }, [dispatch, user, profile]);

  // Check if profile tags contain samsung_platinum
  const hasSamsungPlatinum = (() => {
    if (!profile?.tags) return false;
    let tagList: string[] = [];
    if (Array.isArray(profile.tags)) {
      tagList = profile.tags;
    } else if (typeof profile.tags === 'string') {
      try {
        const parsed = JSON.parse(profile.tags);
        if (Array.isArray(parsed)) {
          tagList = parsed;
        } else if (typeof parsed === 'string') {
          tagList = [parsed];
        }
      } catch {
        tagList = profile.tags.split(',').map((t: string) => t.trim());
      }
    }
    return tagList.some((tag: string) => 
      typeof tag === 'string' && tag.toLowerCase().includes('samsung_platinum')
    );
  })();

  const handleExitImpersonation = () => {
    const returnUrl = localStorage.getItem("admin_return_url") || "/admin/dealers";

    localStorage.removeItem("dealer_token");
    localStorage.removeItem("dealer_role");
    localStorage.removeItem("dealer_user");
    localStorage.removeItem("type");
    localStorage.removeItem("admin_return_url");
    localStorage.removeItem("dealer_survey_dismissed");
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("dealer_inportal_cart")) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn("Failed to clear cart storage on exiting impersonation", e);
    }
    
    if (impersonationType === "url_impersonation") {
      window.location.href = "/login";
    } else {
      window.location.href = returnUrl;
    }
  };

  const handleLogout = async () => {
    if (!isImpersonating) await triggerLogoutActivity("MANUAL_LOGOUT");
    dispatch(logout());
    navigate("/login");
  };

  const impersonatedDealerName = (() => {
    const pFirst = profile?.firstName?.trim() || "";
    const pLast = profile?.lastName?.trim() || "";
    if (pFirst || pLast) return `${pFirst} ${pLast}`.trim();

    const uFirst = user?.firstName?.trim() || "";
    const uLast = user?.lastName?.trim() || "";
    if (uFirst || uLast) return `${uFirst} ${uLast}`.trim();

    try {
      const raw = localStorage.getItem("dealer_user");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.name && typeof parsed.name === "string") return parsed.name.trim();
        const f = parsed.firstName?.trim() || "";
        const l = parsed.lastName?.trim() || "";
        if (f || l) return `${f} ${l}`.trim();
      }
    } catch {
      // ignore
    }
    return "";
  })();

  const impersonatedCompanyName = (() => {
    if (profile?.companyName?.trim()) return profile.companyName.trim();
    if (user?.companyName?.trim()) return user.companyName.trim();

    try {
      const raw = localStorage.getItem("dealer_user");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.companyName && typeof parsed.companyName === "string") return parsed.companyName.trim();
        if (parsed.company && typeof parsed.company === "string") return parsed.company.trim();
      }
    } catch {
      // ignore
    }
    return "";
  })();

  return (
    <nav className="dealer-header w-full px-4 md:px-6 py-1.5 md:py-2 flex items-center justify-between gap-3 border-b relative z-40">
      <style>{`
        @keyframes glossyShimmer {
          0% { transform: translateX(-150%) skewX(-25deg); }
          50%, 100% { transform: translateX(300%) skewX(-25deg); }
        }
        @keyframes aiReportShimmer {
          0% { transform: translateX(-120%) skewX(-15deg); }
          100% { transform: translateX(260%) skewX(-15deg); }
        }
        .ai-report-shimmer::after {
          content: '';
          position: absolute;
          top: 0; left: 0;
          width: 45%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
          animation: aiReportShimmer 2.5s ease-in-out infinite;
          pointer-events: none;
        }
      `}</style>

      <div className="flex items-center gap-2 md:gap-3 shrink-0">
        <Link to="/" className="dealer-header-brand flex items-center gap-2 md:gap-3 hover:opacity-90 transition-opacity shrink-0">
          <img 
            src="/logo.png" 
            alt="DECO TV Frames" 
            className="h-6 md:h-7 w-auto object-contain"
          />
          <span className="dealer-header-wordmark whitespace-nowrap" aria-label="Dealer Portal">
            <span className="dealer-header-wordmark__dealer">Dealer</span>
            <span className="dealer-header-wordmark__portal">Portal</span>
          </span>
        </Link>

        {/* Glossy Shiny Samsung Platinum Badge */}
        {hasSamsungPlatinum && (
          <div 
            title="Samsung Platinum Authorized Dealer"
            className="relative overflow-hidden inline-flex items-center rounded-md select-none transition-transform hover:scale-105 shrink-0"
          >
            {/* Specular Gloss Refraction Layer */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-transparent pointer-events-none rounded-md z-20" />
            
            {/* Shimmering Metallic Bar Animation */}
            <div 
              className="absolute inset-0 z-20 pointer-events-none w-1/2"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.7) 50%, transparent 100%)',
                animation: 'glossyShimmer 3s cubic-bezier(0.4, 0, 0.2, 1) infinite',
              }}
            />

            {/* Samsung Badge Image */}
            <img 
              src="/samsung.png" 
              alt="Samsung Platinum Authorized Dealer" 
              className="h-6 md:h-7 w-auto object-contain relative z-10 drop-shadow-sm"
            />
          </div>
        )}
      </div>

      <div className="dealer-header-actions flex gap-2 md:gap-3 items-center flex-1 min-w-0 justify-end">
        {/* Executive AI Report Button */}
        {user && (
          <button
            onClick={() => setIsAiReportModalOpen(true)}
            title="Generate and Download 2026 Executive AI Performance Report (PDF)"
            className="ai-report-shimmer relative overflow-hidden px-3 md:px-3.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all duration-300 active:scale-95 text-white border border-[#c9a164]/40 shrink-0 cursor-pointer group hover:-translate-y-0.5 hover:brightness-105"
            style={{
              background: 'linear-gradient(135deg, #B08D55 0%, #c9a164 50%, #B08D55 100%)',
              backgroundSize: '200% auto',
              boxShadow: '0 4px 15px rgba(176,141,85,0.35), 0 1px 0 rgba(255,255,255,0.25) inset',
            }}
          >
            <Sparkles className="w-3.5 h-3.5 text-white drop-shadow-sm transition-transform duration-300 group-hover:scale-110" />
            <span className="hidden sm:inline drop-shadow-sm font-semibold tracking-wide">2026 AI Report (PDF)</span>
            <span className="sm:hidden drop-shadow-sm font-semibold">Report</span>
          </button>
        )}

        {/* Portal Feedback Pill Button (Docked / Accessible in top banner) */}
        {user && (
          <button
            onClick={openFeedback}
            title="Open Dealer Portal Feedback"
            className="dealer-feedback-button px-2.5 md:px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all shadow-sm active:scale-95 shrink-0 cursor-pointer"
          >
            <img src={decoIcon} alt="" className="w-3.5 h-3.5 object-contain" />
            <span className="hidden sm:inline">Portal Feedback</span>
          </button>
        )}

        {/* Temporarily hidden as requested by client */}
        {false && user && (
          <button
            onClick={() => {
              const email = user?.businessEmail || profile?.email || "";
              window.location.href = `https://locator.framemytv.com/?email=${encodeURIComponent(email)}&isFirstLoading=true`;
            }}
            className={`px-3 md:px-4 py-1 md:py-1.5 rounded-md md:rounded-lg text-xs font-semibold md:font-bold transition shadow-sm active:scale-95 flex items-center gap-1 md:gap-2 whitespace-nowrap
                 ${profile?.rankingBoostActive
                ? 'bg-green-100 text-green-700 border border-green-200'
                : 'bg-[#B08D55] text-white hover:bg-[#967645]'
              }`}
          >
            {profile?.rankingBoostActive ? <>✅ Boost Active</> : <>Dealer Locator</>}
          </button>
        )}

        {user || isImpersonating ? (
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            {isImpersonating && (
              <div className="flex items-center gap-2 min-w-0">
                {(impersonatedDealerName || impersonatedCompanyName) && (
                  <div className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-lg bg-amber-50/90 border border-amber-200/80 text-[11px] sm:text-xs shadow-sm min-w-0">
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
                    <span className="text-amber-800 font-medium shrink-0">Impersonating:</span>
                    {impersonatedDealerName && (
                      <span className="text-amber-950 font-bold whitespace-nowrap shrink-0" title={impersonatedDealerName}>
                        {impersonatedDealerName}
                      </span>
                    )}
                    {impersonatedDealerName && impersonatedCompanyName && impersonatedCompanyName.toLowerCase() !== impersonatedDealerName.toLowerCase() && (
                      <span className="text-amber-400 font-semibold shrink-0">•</span>
                    )}
                    {impersonatedCompanyName && impersonatedCompanyName.toLowerCase() !== impersonatedDealerName?.toLowerCase() && (
                      <span className="text-amber-800 font-medium min-w-0 truncate" title={impersonatedCompanyName}>
                        {impersonatedCompanyName}
                      </span>
                    )}
                  </div>
                )}
                <button
                  onClick={handleExitImpersonation}
                  className="px-2.5 md:px-3 py-1 md:py-1.5 rounded-lg text-xs font-bold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors flex items-center gap-1.5 whitespace-nowrap shadow-sm active:scale-95 shrink-0"
                >
                  Exit Impersonation
                </button>
              </div>
            )}
            {!isImpersonating && (
              <button
                onClick={() => setIsLogoutModalOpen(true)}
                className="p-1.5 text-[#8A6935] hover:bg-[#8A6935]/10 rounded-full transition-colors border border-transparent hover:border-[#8A6935]/30"
                title="Logout"
              >
                <FiLogOut className="w-4 h-4 md:w-5 md:h-5" />
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 md:gap-3">
            <Link
              to="/login"
              className="px-3 md:px-5 py-1 md:py-2 rounded-md md:rounded-lg text-xs md:text-base font-medium text-[#B08D55] hover:bg-[#B08D55]/5 transition-colors"
            >
              Login
            </Link>

            <Link
              to="/register"
              className="px-3 md:px-5 py-1 md:py-2 rounded-md md:rounded-lg text-xs md:text-base font-medium text-white bg-[#B08D55] hover:bg-[#967645] shadow-md shadow-[#B08D55]/20 transition-all active:scale-95"
            >
              Sign Up
            </Link>
          </div>
        )}
      </div>

      <LogoutConfirmModal 
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogout}
      />

      <AiReportModal
        open={isAiReportModalOpen}
        onClose={() => setIsAiReportModalOpen(false)}
        dealerDisplayName={profile?.companyName || (user?.firstName ? `${user.firstName} ${user.lastName}` : "")}
        initialYear={new Date().getFullYear()}
      />
    </nav>
  );
}
